import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Put, v1Post, v1Delete } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, Save, History, Workflow, Trash2 } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Gated } from '@/auth/permissions';
import {
  STAGE_CATALOGUE,
  adaptPipeline,
  adaptStagesFromV1,
  canAddStage,
  formatApiError,
  formatVersionTimestamp,
  stageCatalogueLabel,
  toV1Stages,
} from './pipelines/pipelineUtils';
import { StageRow } from './pipelines/PipelineStageEditor';
import { PartnerQuestionQuotas } from './pipelines/pipelineAssessmentBindings';

export default function PipelinesSettings() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: rawPipelines } = useQuery({ queryKey: ['v1-pipelines'], queryFn: () => v1Get('/pipelines') });
  const pipelines = React.useMemo(() => (rawPipelines || []).map(adaptPipeline), [rawPipelines]);
  const { data: roles } = useQuery({ queryKey: ['v1-master-roles'], queryFn: () => v1Get('/master/roles') });
  const { data: depts } = useQuery({ queryKey: ['v1-master-departments'], queryFn: () => v1Get('/master/departments') });
  const [selectedKey, setSelectedKey] = useState(null);
  const [stages, setStages] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [restoringVersionId, setRestoringVersionId] = useState(null);

  const onSelect = React.useCallback((key) => {
    const p = pipelines.find((x) => x.key === key);
    if (!p) return;
    setSelectedKey(key);
    setStages(p.stages || []);
    const next = new URLSearchParams(searchParams);
    next.set('pipeline', key);
    setSearchParams(next, { replace: true });
  }, [pipelines, searchParams, setSearchParams]);

  useEffect(() => {
    if (!pipelines?.length) return;
    const urlKey = searchParams.get('pipeline');
    const match = urlKey ? pipelines.find((p) => p.key === urlKey) : null;
    const key = match?.key ?? pipelines[0].key;
    const p = pipelines.find((x) => x.key === key);
    setSelectedKey(key);
    setStages(p?.stages || []);
    if (urlKey !== key) {
      const next = new URLSearchParams(searchParams);
      next.set('pipeline', key);
      setSearchParams(next, { replace: true });
    }
  }, [pipelines, searchParams, setSearchParams]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = Number(active.id.split('-')[1]);
    const newIdx = Number(over.id.split('-')[1]);
    // Lock first (RESUME) and last (OFFER if exists)
    const hasOffer = stages[stages.length - 1]?.code === 'OFFER';
    const min = 1; const max = hasOffer ? stages.length - 2 : stages.length - 1;
    if (oldIdx < min || oldIdx > max || newIdx < min || newIdx > max) return;
    setStages(arrayMove(stages, oldIdx, newIdx));
  };

  const addStage = (code) => {
    if (!canAddStage(code, stages)) return;
    const hasOffer = stages[stages.length - 1]?.code === 'OFFER';
    const newStage = {
      code,
      label: code === 'PANEL'
        ? `Technical Panel ${stages.filter((s) => s.code === 'PANEL').length + 1}`
        : stageCatalogueLabel(code),
      code_editor: code === 'PANEL' || code === 'CODE_PANEL',
    };
    if (code === 'OFFER') { setStages([...stages.filter((s) => s.code !== 'OFFER'), newStage]); return; }
    const idx = hasOffer ? stages.length - 1 : stages.length;
    setStages([...stages.slice(0, idx), newStage, ...stages.slice(idx)]);
  };

  const selected = (pipelines || []).find((p) => p.key === selectedKey);
  const stagesDirty = React.useMemo(() => {
    if (!selected?.stages) return false;
    return JSON.stringify(toV1Stages(stages)) !== JSON.stringify(toV1Stages(selected.stages));
  }, [stages, selected?.stages]);

  const save = async () => {
    const pipe = (pipelines || []).find((p) => p.key === selectedKey);
    if (!pipe) return;
    if (!stagesDirty) return;
    if (stages.length < 2) {
      toast.error('A pipeline must include at least two stages (Resume plus one other).');
      return;
    }
    if (stages.some((s) => !(s.label || '').trim())) {
      toast.error('Every stage needs a label before saving.');
      return;
    }
    try {
      const updated = await v1Put(`/pipelines/${pipe._id}/stages`, { stages: toV1Stages(stages) });
      await qc.invalidateQueries({ queryKey: ['v1-pipelines'] });
      await qc.invalidateQueries({ queryKey: ['v1-pipeline-versions', pipe._id] });
      if (updated?.current_version?.stages) {
        setStages(adaptStagesFromV1(updated.current_version.stages));
      }
      toast.success(`${pipe.label} pipeline saved — in-flight candidates keep their prior version.`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const submitDelete = async () => {
    const pipe = (pipelines || []).find((p) => p.key === selectedKey);
    if (!pipe) return;
    setDeleting(true);
    try {
      await v1Delete(`/pipelines/${pipe._id}`);
      const remaining = (pipelines || []).filter((p) => p.key !== selectedKey);
      await qc.invalidateQueries({ queryKey: ['v1-pipelines'] });
      if (remaining.length) {
        onSelect(remaining[0].key);
      } else {
        setSelectedKey(null);
        setStages([]);
        const next = new URLSearchParams(searchParams);
        next.delete('pipeline');
        setSearchParams(next, { replace: true });
      }
      toast.success(`Pipeline "${pipe.label}" deleted.`);
      setDeleteOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const { data: versionHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['v1-pipeline-versions', selected?._id],
    queryFn: () => v1Get(`/pipelines/${selected._id}/versions`),
    enabled: historyOpen && !!selected?._id,
    staleTime: 30_000,
  });

  const restoreVersion = async (versionRow) => {
    if (!selected?._id || !versionRow?.id) return;
    if (!versionRow?.stages?.length) {
      toast.error('That version has no stages to restore.');
      return;
    }
    setRestoringVersionId(versionRow.id);
    try {
      const updated = await v1Post(`/pipelines/${selected._id}/versions/${versionRow.id}/restore`, {});
      await qc.invalidateQueries({ queryKey: ['v1-pipelines'] });
      await qc.invalidateQueries({ queryKey: ['v1-pipeline-versions', selected._id] });
      setStages(adaptStagesFromV1(updated?.current_version?.stages || versionRow.stages));
      setHistoryOpen(false);
      toast.success(`Restored v${versionRow.version} as the current pipeline version.`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, 'Could not restore pipeline version'));
    } finally {
      setRestoringVersionId(null);
    }
  };

  const roleName = (id) => (roles || []).find((r) => r.id === id)?.name || id || '—';
  const deptName = (id) => (depts || []).find((d) => d.id === id)?.name || id || '—';
  const rolesLabel = (p) => {
    if (!p) return '—';
    if (p.roleNames?.length) return p.roleNames.join(', ');
    if (p.roles?.length) return p.roles.map(roleName).join(', ');
    return roleName(p.role);
  };

  const activeRoleIds = React.useMemo(
    () => new Set((roles || []).map((r) => r.id).filter(Boolean)),
    [roles],
  );
  // Delete only when no live master-data role still owns this pipeline.
  const canDeletePipeline = React.useMemo(() => {
    if (!selected) return false;
    const attached = [
      ...(selected.roles || []),
      selected.role,
    ].filter(Boolean);
    if (attached.length === 0) return true;
    return !attached.some((id) => activeRoleIds.has(id));
  }, [selected, activeRoleIds]);

  return (
    <div className="space-y-6">
      <PageHeader title="Pipelines" subtitle="Compose stage workflows per Role-Department. Resume must be first; Offer if present is last." actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-1.5" onClick={() => setHistoryOpen(true)} data-testid="pipeline-history-button"><History className="h-4 w-4" /> History</Button>
          <Gated need="pipeline.edit">
            <p className="text-xs text-muted-foreground max-w-sm text-right">
              Pipelines are created automatically 1:1 with each Role. Configure stages here.
            </p>
          </Gated>
          {selected && (
            <Gated need="pipeline.edit">
              <Button
                variant="outline"
                className="gap-1.5 border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                onClick={() => setDeleteOpen(true)}
                disabled={!canDeletePipeline}
                title={canDeletePipeline ? undefined : 'Delete the attached role first, then you can delete this pipeline.'}
                data-testid="pipeline-delete-button"
              >
                <Trash2 className="h-4 w-4" /> Delete pipeline
              </Button>
            </Gated>
          )}
          <Gated need="pipeline.edit">
            <Button onClick={save} disabled={!stagesDirty || !selected} className="gap-1.5" data-testid="pipeline-save-button"><Save className="h-4 w-4" /> Save version</Button>
          </Gated>
        </div>
      } />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        <Card className="card-elevated flex max-h-[100vh] flex-col overflow-hidden"><CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-3">
          <p className="shrink-0 px-2 pb-2 pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pipelines</p>
          <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto">{(pipelines || []).map((p) => (
            <li key={p.key}><button onClick={() => onSelect(p.key)} data-testid={`pipeline-select-${p.key}`} className={`flex w-full flex-col items-start rounded-lg px-3 py-2 text-left text-sm transition-colors ${selectedKey === p.key ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}>
              <span className="font-medium">{p.label}</span>
              <span className="text-[11px] text-muted-foreground">v{p.version} · {p.stages.length} stages</span>
            </button></li>
          ))}</ul>
        </CardContent></Card>

        <Card className="card-elevated flex max-h-[100vh] flex-col overflow-hidden"><CardContent className="min-h-0 flex-1 overflow-y-auto p-5" data-testid="pipeline-configurator">
          {selected ? (
            <>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-sm font-semibold">{selected.label}</p>
                  <p className="text-xs text-muted-foreground">
                    Version {selected.version} · {rolesLabel(selected)} / {deptName(selected.department)}
                  </p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 rounded-xl border bg-secondary/20 p-3 md:grid-cols-2" data-testid="pipeline-meta-readonly">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Role (1:1, immutable)</label>
                  <p className="mt-1 text-sm" data-testid="pipeline-edit-roles">{rolesLabel(selected)}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Department</label>
                  <p className="mt-1 text-sm" data-testid="pipeline-edit-department">{deptName(selected.department)}</p>
                </div>
              </div>
              {/* Sprint 11.6 (#10) — Tabbed configuration. One tab per stage
                  present in the pipeline + an always-present "Stages" tab to
                  manage the ordered list itself. Eliminates the previous long
                  vertical scroll on pipelines with 5+ stages. */}
              <Tabs defaultValue="stages" className="mt-4">
                <TabsList className="flex w-full flex-wrap justify-start gap-1 bg-secondary/60" data-testid="pipeline-config-tabs">
                  <TabsTrigger value="stages" data-testid="pipeline-tab-stages"><Workflow className="mr-1 h-3.5 w-3.5" /> Stages</TabsTrigger>
                  <TabsTrigger value="quotas" data-testid="pipeline-tab-quotas">Question quotas</TabsTrigger>
                </TabsList>

                <TabsContent value="stages" className="space-y-4">
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={stages.map((_, i) => `s-${i}`)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {stages.map((s, i) => {
                          const locked = i === 0 || (s.code === 'OFFER');
                          return <StageRow key={`${s.code}-${i}`} stage={s} index={i} onChange={(updated) => { const next = [...stages]; next[i] = updated; setStages(next); }} onDelete={() => setStages(stages.filter((_, idx) => idx !== i))} locked={locked} />;
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>
                  <div className="rounded-xl border bg-secondary/40 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Add stage</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {STAGE_CATALOGUE.filter((s) => s.code !== 'RESUME').map((s) => {
                        const eligible = canAddStage(s.code, stages);
                        return (
                          <Button
                            key={s.code}
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                            disabled={!eligible}
                            onClick={() => addStage(s.code)}
                            data-testid={`pipeline-add-stage-${s.code}`}
                            title={eligible ? undefined : `Maximum ${s.instances} already in this pipeline`}
                          >
                            <Plus className="h-3.5 w-3.5" /> {s.label} <span className="text-[10px] text-muted-foreground">{s.instances}</span>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="quotas">
                  {selected._id ? (
                    <PartnerQuestionQuotas
                      pipelineId={selected._id}
                      pipelineLabel={selected.label}
                      stages={stages}
                    />
                  ) : null}
                </TabsContent>
              </Tabs>
            </>
          ) : <p className="text-sm text-muted-foreground">Select a pipeline to configure.</p>}
        </CardContent></Card>
      </div>

      {/* New pipeline dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={(v) => { if (!deleting) setDeleteOpen(v); }}>
        <AlertDialogContent data-testid="delete-pipeline-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this pipeline?</AlertDialogTitle>
            <AlertDialogDescription>
              {canDeletePipeline ? (
                <>
                  <span className="font-medium text-foreground">{selected?.label}</span> is not
                  attached to any active role and will be removed from the Pipelines list. Existing
                  candidates keep their pinned pipeline version. This action is recorded in the audit trail.
                </>
              ) : (
                <>
                  <span className="font-medium text-foreground">{selected?.label}</span> is still
                  attached to an active role. Delete or reassign that role first; role deletion also
                  disables the dedicated pipeline automatically.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-pipeline-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); submitDelete(); }}
              disabled={deleting || !canDeletePipeline}
              className="bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-600"
              data-testid="delete-pipeline-confirm"
            >
              {deleting ? 'Deleting…' : 'Delete pipeline'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Version history dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] gap-0 p-0 overflow-hidden flex flex-col" data-testid="pipeline-history-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle>Pipeline version history</DialogTitle>
            <DialogDescription>
              Every save creates a new immutable version. In-flight candidates continue on the version active when they entered the pipeline.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4">
            {!selected ? (
              <p className="text-sm text-muted-foreground">No pipeline selected.</p>
            ) : historyLoading ? (
              <p className="text-sm text-muted-foreground" data-testid="pipeline-history-loading">Loading version history…</p>
            ) : !(versionHistory || []).length ? (
              <p className="text-sm text-muted-foreground" data-testid="pipeline-history-empty">
                No versions yet. Save the pipeline once to create v1.
              </p>
            ) : (
              <ul className="space-y-2" data-testid="pipeline-history-list">
                {(versionHistory || []).map((v) => {
                  const stageCount = (v.stages || []).length;
                  const isCurrent = v.is_current === true;
                  return (
                    <li
                      key={v.id}
                      className="flex items-center justify-between rounded-xl border bg-card p-3 text-sm"
                      data-testid={`pipeline-history-row-v${v.version}`}
                    >
                      <div>
                        <p className="font-semibold">
                          v{v.version}
                          {isCurrent && (
                            <span className="ml-2 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                              current
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {isCurrent
                            ? `Active · ${formatVersionTimestamp(v.created_at)} · ${stageCount} stage${stageCount === 1 ? '' : 's'}`
                            : `Archived · ${formatVersionTimestamp(v.created_at)} · ${stageCount} stage${stageCount === 1 ? '' : 's'} snapshot`}
                        </p>
                      </div>
                      {isCurrent ? (
                        <span className="text-xs text-muted-foreground">Current</span>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={restoringVersionId === v.id}
                          onClick={() => restoreVersion(v)}
                          data-testid={`pipeline-history-restore-v${v.version}`}
                        >
                          {restoringVersionId === v.id ? 'Restoring…' : 'Restore'}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="ghost" onClick={() => setHistoryOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
